/**
 * usePurchaseInterception Hook
 *
 * High-level hook for purchase interception workflow.
 * Combines WebSocket notifications with API calls for:
 * - Real-time purchase detection
 * - Traffic light decision fetching
 * - Decision history management
 * - Local storage for offline support
 *
 * Features:
 * - Auto-shows modal when purchase detected
 * - Queues purchases if multiple arrive
 * - Stores decision history locally
 * - Syncs with server when online
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/apiClient';
import useShoppingWebSocket, {
  type PurchaseNotification,
  type ConnectionState,
} from './useShoppingWebSocket';

// Types
export interface InterceptedPurchase {
  id: string;
  merchantName: string;
  amount: number;
  category?: string;
  description?: string;
  interceptedAt: Date;
  source: 'websocket' | 'manual' | 'notification';
}

export interface TrafficLightDecision {
  decision: 'green' | 'yellow' | 'red';
  confidence: number;
  reasoning: string;
  impact: {
    immediate: number;
    nextWeek: number;
    nextMonth: number;
  };
  recommendation?: string;
  alternatives?: string[];
}

export interface DecisionRecord {
  purchaseId: string;
  purchase: InterceptedPurchase;
  decision: TrafficLightDecision;
  action: 'approve' | 'delay' | 'decline';
  actionAt: Date;
}

export interface UsePurchaseInterceptionOptions {
  /** Enable WebSocket connection for real-time updates */
  enableRealtime?: boolean;
  /** Auto-show modal on new purchase */
  autoShowModal?: boolean;
  /** Notification permission request */
  requestNotifications?: boolean;
}

export interface UsePurchaseInterceptionReturn {
  /** Currently active purchase (being shown in modal) */
  activePurchase: InterceptedPurchase | null;
  /** Traffic light decision for active purchase */
  activeDecision: TrafficLightDecision | null;
  /** Whether decision is loading */
  isDecisionLoading: boolean;
  /** Decision fetch error */
  decisionError: Error | null;
  /** Whether modal should be shown */
  isModalOpen: boolean;
  /** Queue of pending purchases */
  purchaseQueue: InterceptedPurchase[];
  /** Number of purchases pending review */
  pendingCount: number;
  /** WebSocket connection state */
  connectionState: ConnectionState;
  /** Recent decision history */
  decisionHistory: DecisionRecord[];
  /** User's current balance */
  currentBalance: number;
  /** Open the modal manually */
  openModal: () => void;
  /** Close the modal */
  closeModal: () => void;
  /** Handle user action on purchase */
  handleAction: (action: 'approve' | 'delay' | 'decline') => Promise<void>;
  /** Manually add a purchase to intercept */
  interceptPurchase: (purchase: Omit<InterceptedPurchase, 'id' | 'interceptedAt' | 'source'>) => void;
  /** Clear decision history */
  clearHistory: () => void;
  /** Get today's stats */
  todayStats: {
    approved: number;
    delayed: number;
    declined: number;
    totalAmount: number;
  };
}

// Local storage keys
const STORAGE_KEYS = {
  DECISION_HISTORY: 'flowincash_decision_history',
  PURCHASE_QUEUE: 'flowincash_purchase_queue',
};

// Helper to generate unique IDs
function generateId(): string {
  return `purchase-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// Helper to load from local storage
function loadFromStorage<T>(key: string, defaultValue: T): T {
  try {
    const stored = localStorage.getItem(key);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Convert date strings back to Date objects
      if (Array.isArray(parsed)) {
        return parsed.map((item: any) => ({
          ...item,
          interceptedAt: item.interceptedAt ? new Date(item.interceptedAt) : undefined,
          actionAt: item.actionAt ? new Date(item.actionAt) : undefined,
        })) as T;
      }
      return parsed;
    }
  } catch (error) {
    console.error(`Error loading from storage (${key}):`, error);
  }
  return defaultValue;
}

// Helper to save to local storage
function saveToStorage<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Error saving to storage (${key}):`, error);
  }
}

export function usePurchaseInterception(
  options: UsePurchaseInterceptionOptions = {}
): UsePurchaseInterceptionReturn {
  const {
    enableRealtime = true,
    autoShowModal = true,
    requestNotifications = false,
  } = options;

  const queryClient = useQueryClient();

  // State
  const [purchaseQueue, setPurchaseQueue] = useState<InterceptedPurchase[]>(() =>
    loadFromStorage(STORAGE_KEYS.PURCHASE_QUEUE, [])
  );
  const [decisionHistory, setDecisionHistory] = useState<DecisionRecord[]>(() =>
    loadFromStorage(STORAGE_KEYS.DECISION_HISTORY, [])
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activePurchase, setActivePurchase] = useState<InterceptedPurchase | null>(null);

  // Check if user is authenticated before making API calls
  const isAuthenticated = !!localStorage.getItem('authToken');

  // Fetch current balance (only when authenticated)
  const { data: balanceData } = useQuery({
    queryKey: ['safe-to-spend'],
    queryFn: () => api.financial.getSafeToSpend(),
    staleTime: 60000, // 1 minute
    refetchOnWindowFocus: true,
    enabled: isAuthenticated, // Don't fetch if not authenticated
  });

  const currentBalance = balanceData?.data?.data?.safeToSpend || 0;

  // Fetch traffic light decision for active purchase
  const {
    data: decisionData,
    isLoading: isDecisionLoading,
    error: decisionError,
    refetch: refetchDecision,
  } = useQuery({
    queryKey: ['traffic-light-decision', activePurchase?.id],
    queryFn: async () => {
      if (!activePurchase) return null;

      const response = await api.decisions.getTrafficLight({
        amount: activePurchase.amount,
        description: activePurchase.merchantName,
        category: activePurchase.category,
        includeDetailedAnalysis: true,
      });

      return response.data;
    },
    enabled: !!activePurchase,
    staleTime: 0, // Always fetch fresh decision
    retry: 2,
  });

  // Transform API response to our decision format
  const activeDecision: TrafficLightDecision | null = useMemo(() => {
    if (!decisionData) return null;

    return {
      decision: decisionData.decision,
      confidence: decisionData.confidence,
      reasoning: decisionData.reasoning,
      impact: decisionData.purchaseImpact || {
        immediate: -(activePurchase?.amount ?? 0),
        nextWeek: -(activePurchase?.amount ?? 0),
        nextMonth: -(activePurchase?.amount ?? 0),
      },
      recommendation: decisionData.recommendation,
      alternatives: decisionData.detailedAnalysis?.alternativeSuggestions,
    };
  }, [decisionData, activePurchase?.amount]);

  // Mutation for recording decision
  const recordDecisionMutation = useMutation({
    mutationFn: async (record: DecisionRecord) => {
      // Save to local history
      setDecisionHistory((prev) => {
        const updated = [record, ...prev].slice(0, 100); // Keep last 100
        saveToStorage(STORAGE_KEYS.DECISION_HISTORY, updated);
        return updated;
      });

      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['decision-history'] });
      queryClient.invalidateQueries({ queryKey: ['safe-to-spend'] });

      return record;
    },
  });

  // Handle incoming WebSocket notifications
  const handlePurchaseNotification = useCallback(
    (notification: PurchaseNotification) => {
      if (notification.type === 'captured') {
        const newPurchase: InterceptedPurchase = {
          id: notification.purchaseId || generateId(),
          merchantName: notification.productTitle,
          amount: notification.price,
          interceptedAt: new Date(),
          source: 'websocket',
        };

        // Add to queue
        setPurchaseQueue((prev) => {
          const updated = [...prev, newPurchase];
          saveToStorage(STORAGE_KEYS.PURCHASE_QUEUE, updated);
          return updated;
        });

        // Show browser notification if permitted
        if (requestNotifications && Notification.permission === 'granted') {
          new Notification('Purchase Detected', {
            body: `${newPurchase.merchantName} - $${newPurchase.amount.toFixed(2)}`,
            icon: '/icons/icon-192x192.png',
            tag: newPurchase.id,
          });
        }

        // Auto-show modal if enabled and not already showing
        if (autoShowModal && !isModalOpen) {
          setActivePurchase(newPurchase);
          setIsModalOpen(true);
        }
      }
    },
    [autoShowModal, isModalOpen, requestNotifications]
  );

  // Handle WebSocket purchase updates
  const handlePurchaseUpdate = useCallback((data: any) => {
    // Handle status updates from server
    if (data.status && data.purchaseId) {
      setPurchaseQueue((prev) =>
        prev.filter((p) => p.id !== data.purchaseId)
      );
    }
  }, []);

  // Initialize WebSocket connection
  const { connectionState, sendPurchaseAction } = useShoppingWebSocket({
    autoConnect: enableRealtime,
    onPurchaseNotification: handlePurchaseNotification,
    onPurchaseUpdate: handlePurchaseUpdate,
  });

  // Process queue - show next purchase when modal closes
  useEffect(() => {
    if (!isModalOpen && purchaseQueue.length > 0 && !activePurchase) {
      const nextPurchase = purchaseQueue[0];
      setActivePurchase(nextPurchase);
      if (autoShowModal) {
        setIsModalOpen(true);
      }
    }
  }, [isModalOpen, purchaseQueue, activePurchase, autoShowModal]);

  // Request notification permission
  useEffect(() => {
    if (requestNotifications && 'Notification' in window) {
      if (Notification.permission === 'default') {
        Notification.requestPermission();
      }
    }
  }, [requestNotifications]);

  // Calculate today's stats
  const todayStats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const todayDecisions = decisionHistory.filter(
      (d) => new Date(d.actionAt) >= today
    );

    return {
      approved: todayDecisions.filter((d) => d.action === 'approve').length,
      delayed: todayDecisions.filter((d) => d.action === 'delay').length,
      declined: todayDecisions.filter((d) => d.action === 'decline').length,
      totalAmount: todayDecisions.reduce(
        (sum, d) => sum + (d.action === 'approve' ? d.purchase.amount : 0),
        0
      ),
    };
  }, [decisionHistory]);

  // Open modal
  const openModal = useCallback(() => {
    if (purchaseQueue.length > 0 && !activePurchase) {
      setActivePurchase(purchaseQueue[0]);
    }
    setIsModalOpen(true);
  }, [purchaseQueue, activePurchase]);

  // Close modal
  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    // Don't clear active purchase immediately to allow for animation
    setTimeout(() => {
      setActivePurchase(null);
    }, 300);
  }, []);

  // Handle user action on purchase
  const handleAction = useCallback(
    async (action: 'approve' | 'delay' | 'decline') => {
      if (!activePurchase || !activeDecision) return;

      // Record the decision
      const record: DecisionRecord = {
        purchaseId: activePurchase.id,
        purchase: activePurchase,
        decision: activeDecision,
        action,
        actionAt: new Date(),
      };

      await recordDecisionMutation.mutateAsync(record);

      // Send action to server via WebSocket
      if (connectionState.isConnected && connectionState.isAuthenticated) {
        const wsAction = action === 'approve' ? 'approve' : action === 'decline' ? 'reject' : 'delay';
        sendPurchaseAction(activePurchase.id, wsAction);
      }

      // Remove from queue
      setPurchaseQueue((prev) => {
        const updated = prev.filter((p) => p.id !== activePurchase.id);
        saveToStorage(STORAGE_KEYS.PURCHASE_QUEUE, updated);
        return updated;
      });

      // Close modal
      closeModal();
    },
    [
      activePurchase,
      activeDecision,
      recordDecisionMutation,
      connectionState,
      sendPurchaseAction,
      closeModal,
    ]
  );

  // Manually intercept a purchase
  const interceptPurchase = useCallback(
    (purchaseData: Omit<InterceptedPurchase, 'id' | 'interceptedAt' | 'source'>) => {
      const newPurchase: InterceptedPurchase = {
        ...purchaseData,
        id: generateId(),
        interceptedAt: new Date(),
        source: 'manual',
      };

      setPurchaseQueue((prev) => {
        const updated = [...prev, newPurchase];
        saveToStorage(STORAGE_KEYS.PURCHASE_QUEUE, updated);
        return updated;
      });

      // If no active purchase, set this one and optionally show modal
      if (!activePurchase) {
        setActivePurchase(newPurchase);
        if (autoShowModal) {
          setIsModalOpen(true);
        }
      }
    },
    [activePurchase, autoShowModal]
  );

  // Clear decision history
  const clearHistory = useCallback(() => {
    setDecisionHistory([]);
    saveToStorage(STORAGE_KEYS.DECISION_HISTORY, []);
  }, []);

  return {
    activePurchase,
    activeDecision,
    isDecisionLoading,
    decisionError: decisionError as Error | null,
    isModalOpen,
    purchaseQueue,
    pendingCount: purchaseQueue.length,
    connectionState,
    decisionHistory,
    currentBalance,
    openModal,
    closeModal,
    handleAction,
    interceptPurchase,
    clearHistory,
    todayStats,
  };
}

export default usePurchaseInterception;
